import React, { useEffect, useState } from 'react';

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { fetchCalendar, insertSlot, leaveDetails, bulkDeleteFreeSlots } from '../../../../../Api/apiUtils';
import './Calendar.css';
import './calendar.scss';
import Colorbox from './colorbox';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup } from 'reactstrap';
import { Button, DescriptionList, Field, Loader, Select, TextInput } from '../../../../../ui';
import TextArea from '../ui-pending/TextArea';
import EventModal from './modal';
import { toast } from 'react-toastify';
import { constant } from '../../../../../Api/mixpanel';
import { MixpanelEvents, trackEvent } from "../../../../../utils/mixpanelTracking";
import { FontSize, Important } from '../../../../../utils/Constant';
import colors from 'react-multi-date-picker/plugins/colors';
import { formatDate } from '../../../../../utils/formatDate';
import { isTeacherLeaderDisabled } from '../../../../../utils/permissions';

function todayLocalYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const toAmPm = (hhmm: string) => {
  if (!hhmm || typeof hhmm !== "string") return "—";
  const parts = hhmm.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "—";
  const hour12 = h % 12 || 12;
  const suffix = h >= 12 ? "PM" : "AM";
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
};

/** 30-minute slots; labels like 12:00 AM, 12:30 AM, 1:00 AM — values sent as HH:mm. */
const BULK_DELETE_TIME_OPTIONS_30 = Array.from({ length: 48 }, (_, index) => {
  const hour = String(Math.floor(index / 2)).padStart(2, "0");
  const minute = index % 2 === 0 ? "00" : "30";
  const value = `${hour}:${minute}`;
  return { value, label: toAmPm(value) };
});

interface CalendarProps {
  teacherId: any;
  teacherProfileData: any;
}

// Add interface for calendar events
interface CalendarEvent {
  classStatus?: string;
  start: string;
  end: string;
  backgroundColor: string;
  borderColor: string;
  title: string;
  extendedProps: any;
}

const Calendar :  React.FC<CalendarProps> =( {teacherId, teacherProfileData}) => {
  const readOnly = isTeacherLeaderDisabled();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState({
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]
  });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState({
    startTime: "",
    endTime: "",
    slotDate: "",
    specialSlot: false,
  });

  const [eventDetails, setEventDetails] = useState({
    classId: "",
    teacherName: "",
    classBookingStatus: null,
    classStatus: "",
    startTime: "",
    endTime: "",
    slotDate: "",
    studentUserId: "",
  });
  const [calendarRef, setCalendarRef] = useState<any>(null);

  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkFromDate, setBulkFromDate] = useState("");
  const [bulkToDate, setBulkToDate] = useState("");
  /** UI choice → API sends scope: PAID / AIRLINE / both */
  const [bulkScopeChoice, setBulkScopeChoice] = useState<"paid" | "airline" | "both">("both");
  const [bulkDeleteReason, setBulkDeleteReason] = useState("");
  const [bulkStartTime, setBulkStartTime] = useState("");
  const [bulkEndTime, setBulkEndTime] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const minBulkDateStr = todayLocalYmd();

  const closeBulkDeleteModal = () => {
    if (!bulkDeleting) setBulkDeleteModalOpen(false);
  };

  const openBulkDeleteModal = () => {
    if (readOnly) return;
    const t = todayLocalYmd();
    setBulkFromDate(t);
    setBulkToDate(t);
    setBulkScopeChoice("both");
    setBulkDeleteReason("");
    setBulkStartTime("");
    setBulkEndTime("");
    setBulkDeleteModalOpen(true);
  };

  const normalizeBulkTime = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const parts = trimmed.split(":");
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
    }
    return trimmed;
  };

  const bulkTimeToMinutes = (value: string): number => {
    const normalized = normalizeBulkTime(value);
    const [h, m] = normalized.split(":").map(Number);
    return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
  };

  const handleConfirmBulkDeleteFreeSlots = async () => {
    if (readOnly) return;
    const tidNum = teacherId != null && teacherId !== "" ? Number(teacherId) : NaN;
    if (!Number.isFinite(tidNum)) {
      toast.error("Missing or invalid teacher id.");
      return;
    }
    if (!bulkFromDate.trim() || !bulkToDate.trim()) {
      toast.error("Please select both from date and to date.");
      return;
    }
    if (bulkFromDate < minBulkDateStr || bulkToDate < minBulkDateStr) {
      toast.error("Past dates cannot be selected. Choose today or a future date.");
      return;
    }
    if (bulkFromDate > bulkToDate) {
      toast.error("From date must be on or before to date.");
      return;
    }
    const reasonTrimmed = bulkDeleteReason.trim();
    if (!reasonTrimmed) {
      toast.error("Please enter a reason for deleting free slots.");
      return;
    }
    let scopeArr: Array<"AIRLINE" | "PAID">;
    if (bulkScopeChoice === "both") {
      scopeArr = ["AIRLINE", "PAID"];
    } else if (bulkScopeChoice === "paid") {
      scopeArr = ["PAID"];
    } else {
      scopeArr = ["AIRLINE"];
    }

    const startTimeTrimmed = bulkStartTime.trim();
    const endTimeTrimmed = bulkEndTime.trim();
    const hasStartTime = Boolean(startTimeTrimmed);
    const hasEndTime = Boolean(endTimeTrimmed);

    if (hasStartTime !== hasEndTime) {
      toast.error("Please select both start and end time, or leave both empty.");
      return;
    }

    let timings: Array<{ startTime: string; endTime: string }> | undefined;
    if (hasStartTime && hasEndTime) {
      const startTime = normalizeBulkTime(startTimeTrimmed);
      const endTime = normalizeBulkTime(endTimeTrimmed);
      if (bulkTimeToMinutes(endTime) <= bulkTimeToMinutes(startTime)) {
        toast.error("End time must be after start time.");
        return;
      }
      timings = [{ startTime, endTime }];
    }

    setBulkDeleting(true);
    try {
      await bulkDeleteFreeSlots({
        teacherId: tidNum,
        fromDate: bulkFromDate,
        toDate: bulkToDate,
        scope: scopeArr,
        ...(timings ? { timings } : {}),
      });
      trackEvent(MixpanelEvents.TEACHER_BULK_DELETE_FREE_SLOTS, {
        page: "Teacher Calendar",
        reason: reasonTrimmed,
        teacherName: teacherProfileData?.teacherProfileDetails?.firstName + " " + teacherProfileData?.teacherProfileDetails?.lastName,
        teacherId: tidNum,
        teacherUserId: localStorage.getItem("teacherProfileUserId") ?? "",
        fromDate: bulkFromDate,
        toDate: bulkToDate,
        scope: scopeArr,
        slotScopeLabel: bulkScopeChoice,
        ...(timings ? { timings } : {}),
      });
      toast.success("Free slots deletion completed.");
      setBulkDeleteReason("");
      setBulkStartTime("");
      setBulkEndTime("");
      setBulkDeleteModalOpen(false);
      fetchCalendarData(dateRange.fromDate, dateRange.toDate);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        ax?.response?.data?.message ||
        ax?.response?.data?.error ||
        (err instanceof Error ? err.message : "Failed to bulk-delete free slots.");
      toast.error(String(msg));
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && isConfirmModalOpen) {
        handleConfirmSlot();
      }
    };
  
    if (isConfirmModalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
  
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isConfirmModalOpen]);
  const fetchCalendarData = async (fromDate: string, toDate: string) => {
    setLoading(true);
    try {
      const storedTeacherId = localStorage.getItem("teacherProfileUserId");
      if (!storedTeacherId) {
        console.error("Missing teacherUserId in local storage.");
        setEvents([]);
        return;
      }

      const payload = {
        fromDate,
        toDate,
        teacherUserId: storedTeacherId,
        actionBy: "teacher",
      };

      let mappedEvents: CalendarEvent[] = [];
      try {
        const response = await fetchCalendar(payload);
        mappedEvents = response.map((event: any) => {
          let backgroundColor = "#bed197"; // Default (Free Slot)
          let borderColor = "#bed197";
          let title = "Free Slot"; // Default title

          if (event.classBookingStatus === "COMPLETED") {
            backgroundColor = "grey"; // Gray
            borderColor = "grey"; // Red border
            title = "Completed";
          } else if (
            String(event.classBookingStatus || "").toLowerCase() === "deleted"
          ) {
            backgroundColor = "#9ca3af";
            borderColor = "#9ca3af";
            title = "Cancelled";
          } else if (
            event.classBookingStatus === "NOT_SCHEDULED" ||
            event.classBookingStatus === "SCHEDULED" ||
            event.classBookingStatus === "RESCHEDULED"
          ) {
            backgroundColor = "#ffc306"; // Yellow
            borderColor = "#ffc306"; // Yellow border
            title = "Booked";
          } else if (
            event.classBookingStatus === "TEACHER_NOSHOW" ||
            event.classBookingStatus === "PAID_NOSHOW"
          ) {
            backgroundColor = "#7b7e80"; // Dark gray
            borderColor = "#7b7e80"; // Gray border
            title = "No Show";
          } else if (event.specialSlot === true) {
            backgroundColor = "palevioletred"; // Pink for special slots
            borderColor = "palevioletred";
            title = "Special Free Slot";
          } else if (event.title) {
            backgroundColor = "#e82c86";
            borderColor = "#e82c86";
            title = event.title;
          }

          if (event.courseName && event.isBooked === "true") {
            title = event.courseName;
          } else if (event.isBooked === "true") {
            title = "-";
          }
          const displayEndTime = event.endTime === "00:00:00" ? "23:59:59" : event.endTime;

          return {
            classStatus: event.classBookingStatus,
            start: `${event.slotDate}T${event.startTime}`,
            end: `${event.slotDate}T${displayEndTime}`,
            backgroundColor,
            borderColor,
            title,
            extendedProps: event,
          };
        });
      } catch (error) {
        console.error("Error fetching calendar slots:", error);
      }

      let mappedLeaveEvents: CalendarEvent[] = [];
      try {
        const leaveResponse = await leaveDetails(teacherId);
        console.log("Leacreponse", leaveResponse);
        const approvedLeaves = leaveResponse.content.filter(
          (leave: any) => leave.leaveStatus?.type === "leave_approved",
        );
        mappedLeaveEvents = approvedLeaves.flatMap((leave: any) => {
          const leaveFromDate = leave.fromDate.split(" ")[0];
          const leaveToDate = leave.toDate.split(" ")[0];
          const start = `${leaveFromDate}T${leave.startTime}`;
          const end = `${leaveToDate}T${leave.endTime}`;

          const endAllDay = new Date(leaveToDate);
          endAllDay.setDate(endAllDay.getDate() + 1);
          const endAllDayStr = endAllDay.toISOString().split("T")[0];

          const monthEvent = {
            start: leaveFromDate,
            end: endAllDayStr,
            backgroundColor: "#ff5151",
            borderColor: "#ff5151",
            title: `Leave\n${formatDate(`${leaveFromDate}T${leave.startTime}`, "timeOnly")}\n to \n${formatDate(
              `${leaveToDate}T${leave.endTime}`,
              "timeOnly",
            )}`,
            allDay: true,
            editable: false,
            display: "auto",
          };

          const backgroundEvent = {
            start,
            end,
            backgroundColor: "#ff5151",
            borderColor: "#ff5151",
            title: "Leave",
            color: "red",
            allDay: false,
            editable: false,
            display: "background",
          };

          return [monthEvent, backgroundEvent];
        });
      } catch (error) {
        console.error("Error fetching leave details:", error);
      }

      setEvents([...mappedEvents, ...mappedLeaveEvents]);
    } finally {
      setLoading(false);
    }
  };

  const handleDatesSet = (arg: any) => {
    const fromDate = arg.start.toISOString().split('T')[0];
    const toDate = arg.end.toISOString().split('T')[0];
    setDateRange({ fromDate, toDate });
    fetchCalendarData(fromDate, toDate);
  };
  const handleMoreLinkClick = (date: any) => {
    if (calendarRef) {
      calendarRef.getApi().changeView("timeGridDay", date);
    }
  };

  const handleEventClick = (info: any) => {
    // Check if the event is a leave event
    const isLeaveEvent = info.event.extendedProps?.isLeave || 
                        info.event.title?.includes('Leave') || 
                        info.event.backgroundColor === '#ff5151';
    
    if (!isLeaveEvent) {
      setEventDetails(info.event.extendedProps);
      setIsModalOpen(true);
    }
  };

  const handleDateClick = (arg: any) => {
    setIsConfirmModalOpen(false);
    if (calendarRef) {
      calendarRef.getApi().changeView('timeGridDay', arg.date);
    }
  };

const handleSelect = (selectInfo: any) => {
  if (readOnly) {
    selectInfo.view.calendar.unselect();
    return;
  }
  // Create time display element
  const timeDisplay = document.createElement('div');
  timeDisplay.className = 'time-selection-feedback';
  document.body.appendChild(timeDisplay);

  // Function to update time display
  const updateTimeDisplay = () => {
    const startDate = new Date(selectInfo.startStr);
    const endDate = new Date(selectInfo.endStr);
    
    const formatTime = (date: Date) => {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    
    const durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
    timeDisplay.textContent = `${formatTime(startDate)} - ${formatTime(endDate)} (${durationMinutes} mins)`;
    
    // Position near cursor
    const mouseEvent = selectInfo.jsEvent;
    timeDisplay.style.left = `${mouseEvent.clientX + 15}px`;
    timeDisplay.style.top = `${mouseEvent.clientY + 15}px`;
  };

  // Track mouse movement
  const handleMouseMove = (e: MouseEvent) => {
    updateTimeDisplay();
  };

  // Clean up when done
  const cleanup = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.body.removeChild(timeDisplay);
    document.removeEventListener('mouseup', cleanup);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', cleanup);

  // Initial update
  updateTimeDisplay();

  // Your existing validation logic
  const startDate = new Date(selectInfo.startStr);
  const endDate = new Date(selectInfo.endStr);
  const durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
  if (durationMinutes % 30 !== 0) {
    toast.error("Please select slots in 30-minute increments (30, 60, 90, etc.)");
    selectInfo.view.calendar.unselect();
    cleanup();
    return;
}

  const currentTime = new Date();
  if (startDate < currentTime) {
    toast.error("You can only select slots after the current time.");
    selectInfo.view.calendar.unselect();
    cleanup();
    return;
  }

  const endTimeStr = endDate.toTimeString().split(' ')[0];
  // A selection that runs to midnight ends at 00:00 of the next day; send 00:00:00 (not 23:59:59)
  // so insertteacherslot stores midnight as end-of-day correctly.
  const endsAtMidnight = endTimeStr === '00:00:00' && endDate.getDate() !== startDate.getDate();
  const adjustedEndTime = endsAtMidnight ? '00:00:00' : endTimeStr;

  // Check for overlap with leave events
  const overlappingLeave = events.some(event => {
    if (event.extendedProps?.isLeave) {
      const leaveStart = new Date(event.start);
      const leaveEnd = new Date(event.end);
      // If the selected slot overlaps with the leave event
      return (
        (startDate < leaveEnd && endDate > leaveStart)
      );
    }
    return false;
  });

  if (overlappingLeave) {
    toast.warn("Warning: You are booking a slot during a leave period!");
  }

  setSelectedSlot({
    startTime: startDate.toTimeString().split(' ')[0],
    endTime: adjustedEndTime,
    slotDate: selectInfo.startStr.split('T')[0],
    specialSlot: false,
  });

  setIsConfirmModalOpen(true);
  cleanup();
};
const handleConfirmSlot = async () => {
  if (readOnly) return;
  const payload = [
    {
      teacherId: teacherId,
      startTime: selectedSlot.startTime,
      EndTime: selectedSlot.endTime,
      slotDate: selectedSlot.slotDate,
      specialSlot: selectedSlot.specialSlot,
    },
  ];

  try {
    await insertSlot(payload);
    toast.success("Slot created successfully!");
    trackEvent(MixpanelEvents.CALENDAR_SLOT_INSERTED, {
      calendarView: "teacher",
      teacherId: teacherId != null ? String(teacherId) : undefined,
      slotDate: selectedSlot.slotDate,
      specialSlot: selectedSlot.specialSlot,
    });
    setIsConfirmModalOpen(false);
    fetchCalendarData(dateRange.fromDate, dateRange.toDate);
  } catch (error: any) {
    console.error("Error inserting slot:", error);
    trackEvent(MixpanelEvents.CALENDAR_SLOT_INSERT_FAILED, {
      calendarView: "teacher",
      teacherId: teacherId != null ? String(teacherId) : undefined,
    });
    
    // Check if this is a slot already exists error
    if (error.response?.status === 400 && 
        error.response?.data?.["TeacherSlot already exist!"] === true) {
      toast.error("This slot already exists for the teacher!");
    } else {
      toast.error("Failed to create slot. Please try again.");
    }
  }
};

  const bulkTimeOptions = [
    { value: "", label: "Select time" },
    ...BULK_DELETE_TIME_OPTIONS_30,
  ];

  return (
    <div className="tp-cal">
      <div className="tp-cal__toolbar">
        <div className="tp-cal__toolbar-actions">
          <Button
            variant="secondary"
            size="sm"
            disabled={readOnly}
            title={readOnly ? "Disabled for Teacher Leaders" : undefined}
            onClick={openBulkDeleteModal}
          >
            Bulk delete free slots
          </Button>
        </div>
        <div className="tp-cal__legend">
          <Colorbox />
        </div>
      </div>
      <div className="tp-cal__stage">
        {loading && (
          <div className="tp-cal__overlay">
            <Loader label="Loading calendar…" />
          </div>
        )}

<FullCalendar
  ref={setCalendarRef}
  initialView="dayGridMonth"
  initialDate={new Date()}
  headerToolbar={{
    left: "prev,next today",
    center: "title",
    right: "dayGridMonth,timeGridWeek,timeGridDay",
  }}
  views={{
    timeGridWeek: {
      dayHeaderFormat: { weekday: "short",  day: "numeric" }, 
    },
  }}
  displayEventTime={false} 
  displayEventEnd={false} 
  editable={!readOnly}
  selectable={!readOnly}
  droppable={!readOnly}
  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
  events={events}
  datesSet={handleDatesSet}
  eventClick={handleEventClick}
  dayMaxEventRows={2}
  moreLinkClick={(info) => handleMoreLinkClick(info.date)}
  dateClick={handleDateClick}
  select={handleSelect}

  contentHeight={600} 
  allDaySlot={false} 
  slotDuration="00:15:00" 

  slotLabelInterval="00:30" 
  // selectAllow={(selectInfo) => {
  //   const start = new Date(selectInfo.startStr);
  //   const end = new Date(selectInfo.endStr);
  //   const duration = (end.getTime() - start.getTime()) / (1000 * 60);
  //   return duration === 30 || duration === 60;
  // }}
  eventContent={(arg) => {
    const currentView = arg.view.type; 
    const startTime = arg.event.start?.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    const endTime = arg.event.end?.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const titleLines = (arg.event.title || "").split("\n");

    return (
      <div
        style={{
          backgroundColor: arg.event.backgroundColor,
          padding: "5px",
          borderRadius: "5px",
          color: "#fff",
          fontWeight: "bold",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {(currentView === "timeGridDay" || currentView === "timeGridWeek") && (
          <div style={{ marginBottom: "2px" }}>
            {startTime} - {endTime}
          </div>
        )}
        <div style={{ textAlign: "center", lineHeight: 1.2 }}>
          {titleLines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      </div>
    );
  }}
/>

      </div>

      {/* Bulk delete free slots */}
      <Modal
        isOpen={bulkDeleteModalOpen}
        toggle={closeBulkDeleteModal}
        backdrop={bulkDeleting ? "static" : true}
      >
        <ModalHeader toggle={closeBulkDeleteModal}>
          Bulk delete free slots
        </ModalHeader>
        <ModalBody className="tp-cal-bulk">
          <p className="tp-cal-bulk__note">
            Only <strong>free</strong> slots in the chosen date range will be removed not the paid slots.
          </p>
          <Field label="Reason" required id="bulkDeleteReason">
            {(control) => (
              <TextArea
                {...control}
                rows={3}
                placeholder="Explain why free slots are being removed (required)"
                value={bulkDeleteReason}
                onChange={(e) => setBulkDeleteReason(e.target.value)}
                disabled={bulkDeleting}
                required
                maxLength={2000}
              />
            )}
          </Field>
          <Field label="From date" id="bulkFromDate">
            {(control) => (
              <TextInput
                {...control}
                type="date"
                min={minBulkDateStr}
                value={bulkFromDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setBulkFromDate(v);
                  if (bulkToDate && bulkToDate < v) setBulkToDate(v);
                }}
                disabled={bulkDeleting}
              />
            )}
          </Field>
          <Field label="To date" id="bulkToDate">
            {(control) => (
              <TextInput
                {...control}
                type="date"
                min={bulkFromDate >= minBulkDateStr ? bulkFromDate : minBulkDateStr}
                value={bulkToDate}
                onChange={(e) => {
                  const v = e.target.value;
                  if (bulkFromDate && v < bulkFromDate) setBulkToDate(bulkFromDate);
                  else setBulkToDate(v);
                }}
                disabled={bulkDeleting}
              />
            )}
          </Field>
          <Field label="Slot type" id="bulkScope">
            {(control) => (
              <Select
                {...control}
                value={bulkScopeChoice}
                onChange={(e) =>
                  setBulkScopeChoice(e.target.value as "paid" | "airline" | "both")
                }
                disabled={bulkDeleting}
                options={[
                  { value: "paid", label: "Paid only" },
                  { value: "airline", label: "Airline (AE) only" },
                  { value: "both", label: "Paid + Airline (both)" },
                ]}
              />
            )}
          </Field>
          <fieldset className="tp-cal-bulk__time-range">
            <legend className="ds-field__label">Time range (optional)</legend>
            <p className="tp-cal-bulk__note">
              Leave empty to delete free slots for the full day. Select a start and end time to limit deletion to that window.
            </p>
            <div className="tp-cal-bulk__time-fields">
              <Field label="Start" id="bulkStartTime">
                {(control) => (
                  <Select
                    {...control}
                    size="sm"
                    value={bulkStartTime}
                    onChange={(e) => setBulkStartTime(e.target.value)}
                    disabled={bulkDeleting}
                    options={bulkTimeOptions}
                  />
                )}
              </Field>
              <Field label="End" id="bulkEndTime">
                {(control) => (
                  <Select
                    {...control}
                    size="sm"
                    value={bulkEndTime}
                    onChange={(e) => setBulkEndTime(e.target.value)}
                    disabled={bulkDeleting}
                    options={bulkTimeOptions}
                  />
                )}
              </Field>
            </div>
          </fieldset>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" disabled={bulkDeleting} onClick={() => closeBulkDeleteModal()}>
            Cancel
          </Button>
          <Button variant="danger" loading={bulkDeleting} onClick={() => void handleConfirmBulkDeleteFreeSlots()}>
            Confirm delete
          </Button>
        </ModalFooter>
      </Modal>

      {/* Event Details Modal */}
      <EventModal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        eventDetails={eventDetails}
        fetchCalendarData={fetchCalendarData}
        currentDateRange={dateRange} 
      />

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmModalOpen} toggle={() => setIsConfirmModalOpen(false)}>
        <ModalHeader toggle={() => setIsConfirmModalOpen(false)}>Confirm Selected Slot</ModalHeader>
        <ModalBody className="tp-cal-confirm">
          <DescriptionList
            items={[
              { label: "Date", value: selectedSlot.slotDate },
              { label: "Time range", value: `${selectedSlot.startTime} - ${selectedSlot.endTime}` },
            ]}
          />
          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                checked={selectedSlot.specialSlot}
                disabled={readOnly}
                onChange={(e) => {
                  if (readOnly) return;
                  setSelectedSlot({ ...selectedSlot, specialSlot: e.target.checked });
                }}
              />{' '}
              Special Slot
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={handleConfirmSlot} disabled={readOnly} title={readOnly ? "Disabled for Teacher Leaders" : undefined}>Confirm</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default Calendar;
